/**
 * Wholesale Price Tooltip Enhancement
 * Adds hover tooltips showing price breakdowns for wholesale items
 */

class WholesalePriceTooltip {
    constructor() {
        this.tooltip = null;

        // Tax data with carton sizes - federal/provincial taxes are per CARTON
        this.taxData = {
            'NASHA10K': {
                cartonSize: 5,
                provincial: { federalTax: 16.80, provincialTax: 16.80 },
                federal: { federalTax: 16.80, provincialTax: 0 }
            },
            'NASHA25K': {
                cartonSize: 5,
                provincial: { federalTax: 33.60, provincialTax: 33.60 },
                federal: { federalTax: 33.60, provincialTax: 0 }
            },
            'NASHA40K': {
                cartonSize: 4,
                provincial: { federalTax: 31.36, provincialTax: 31.36 },
                federal: { federalTax: 31.36, provincialTax: 0 }
            },
            'NASHA-E-LIQUID': {
                cartonSize: 1,
                provincial: { federalTax: 7.84, provincialTax: 7.84 },
                federal: null // Not offered
            },
            // If pack-level taxes change for the 5-pack rollout, update Ultra 1 here too.
            'BOOSTED-ULTRA-POD-1': {
                cartonSize: 5,
                provincial: { federalTax: 16.80, provincialTax: 16.80 },
                federal: null // Not offered
            },
            'BOOSTED-ULTRA-POD-2': {
                cartonSize: 5,
                provincial: { federalTax: 33.60, provincialTax: 33.60 },
                federal: null // Not offered
            },
            'BOOSTED30K': {
                cartonSize: 5,
                provincial: { federalTax: 39.20, provincialTax: 39.20 },
                federal: { federalTax: 39.20, provincialTax: 0 }
            },
            'BOOSTED-BAR-2ML': {
                cartonSize: 10,
                provincial: { federalTax: 11.20, provincialTax: 11.20 },
                federal: { federalTax: 11.20, provincialTax: 0 }
            },
            'BOOSTED-DROP-SHOTS': {
                cartonSize: 10,
                provincial: { federalTax: 0, provincialTax: 0 }, // No tax
                federal: { federalTax: 0, provincialTax: 0 }
            },
            // Boosted 7D 90K - same tax rates as Nasha 40K
            'BOOSTED-7D-90K': {
                cartonSize: 4,
                provincial: { federalTax: 31.36, provincialTax: 31.36 },
                federal: { federalTax: 31.36, provincialTax: 0 }
            }
        };

        this.init();
    }

    init() {
        this.createTooltipElement();
        this.attachEventListeners();

        // Watch for dynamic content changes (wholesale app may load content dynamically)
        this.observeDOM();
    }

    createTooltipElement() {
        this.tooltip = document.createElement('div');
        this.tooltip.className = 'wholesale-price-tooltip';
        this.tooltip.innerHTML = `
            <div class="tooltip-content">
                <div class="tooltip-header">
                    <h4><span id="tooltipTierLabel"></span>Price Breakdown</h4>
                </div>
                
                <div class="breakdown-section unit-section">
                    <div class="section-title">Per Unit</div>
                    <div class="breakdown-item device-original-row" id="deviceOriginalRow">
                        <span class="label">Base Unit Cost:</span>
                        <span class="value unit-device-original"></span>
                    </div>
                    <div class="breakdown-item device-discount-row" id="deviceDiscountRow">
                        <span class="label">Your Discount:</span>
                        <span class="value unit-device-discount"></span>
                    </div>
                    <div class="breakdown-item">
                        <span class="label">Your Unit Cost:</span>
                        <span class="value unit-device-cost"></span>
                    </div>
                    <div class="breakdown-item">
                        <span class="label">Federal Tax:</span>
                        <span class="value unit-federal-tax"></span>
                    </div>
                    <div class="breakdown-item provincial-tax-row">
                        <span class="label">Provincial Tax:</span>
                        <span class="value unit-provincial-tax"></span>
                    </div>
                    <div class="breakdown-item total-row">
                        <span class="label">Unit Total:</span>
                        <span class="value unit-total"></span>
                    </div>
                </div>
                
                <div class="breakdown-section carton-section">
                    <div class="section-title">Per Carton <span class="carton-qty-badge"></span></div>
                    <div class="breakdown-item">
                        <span class="label">Devices:</span>
                        <span class="value carton-device-cost"></span>
                    </div>
                    <div class="breakdown-item">
                        <span class="label">Total Tax:</span>
                        <span class="value carton-tax"></span>
                    </div>
                    <div class="breakdown-item total-row">
                        <span class="label">Carton Total:</span>
                        <span class="value carton-total"></span>
                    </div>
                </div>
                
                <div class="breakdown-section savings-section" id="tooltipSavingsSection" style="display: none;">
                    <div class="section-title">Your Tier Savings</div>
                    <div class="breakdown-item">
                        <span class="label">Regular Price:</span>
                        <span class="value savings-original"></span>
                    </div>
                    <div class="breakdown-item">
                        <span class="label">Your Price:</span>
                        <span class="value savings-discounted"></span>
                    </div>
                    <div class="breakdown-item savings-row">
                        <span class="label">You Save:</span>
                        <span class="value savings-amount"></span>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(this.tooltip);
    }

    attachEventListeners() {
        let isTooltipVisible = false;
        let tooltipTimeout = null;
        let currentPriceElement = null; // Track which element we're showing tooltip for

        document.addEventListener('mousemove', (e) => {
            const priceElement = this.findPriceElementAtPosition(e.clientX, e.clientY);

            if (priceElement && this.isHoveringInfoIcon(e, priceElement)) {
                // Clear any pending hide timeout
                if (tooltipTimeout) {
                    clearTimeout(tooltipTimeout);
                    tooltipTimeout = null;
                }

                // Refresh tooltip if hovering a different element
                if (!isTooltipVisible || currentPriceElement !== priceElement) {
                    this.showTooltip(e, priceElement);
                    isTooltipVisible = true;
                    currentPriceElement = priceElement;
                } else {

                    this.positionTooltip(e);
                }
            } else {
                if (isTooltipVisible && !tooltipTimeout) {
                    // Add small delay to prevent flickering
                    tooltipTimeout = setTimeout(() => {
                        this.hideTooltip();
                        isTooltipVisible = false;
                        currentPriceElement = null;
                        tooltipTimeout = null;
                    }, 100);
                }
            }
        });

        // Mobile: dismiss tooltip on touch outside
        document.addEventListener('touchstart', (e) => {
            if (isTooltipVisible && this.tooltip && !this.tooltip.contains(e.target)) {
                // Check if NOT tapping an info icon
                const tappedElement = e.target.closest('.nm-easywholesale-price');
                if (!tappedElement) {
                    this.hideTooltip();
                    isTooltipVisible = false;
                    currentPriceElement = null;
                }
            }
        });

        // Mobile: dismiss tooltip on scroll
        document.addEventListener('scroll', () => {
            if (isTooltipVisible) {
                this.hideTooltip();
                isTooltipVisible = false;
                currentPriceElement = null;
            }
        }, { passive: true });
    }

    findPriceElementAtPosition(x, y) {
        // Only target row prices, not header prices, to avoid stray icons/tooltip near titles
        const elements = document.querySelectorAll('.nm-easywholesale-price');

        for (let element of elements) {
            const rect = element.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                return element;
            }
        }
        return null;
    }

    isHoveringInfoIcon(event, priceElement) {
        // Check if cursor is in the left portion of the price element (where icon is)
        const rect = priceElement.getBoundingClientRect();
        const iconAreaEnd = rect.left + 35; // First 35px of the price element

        return event.clientX >= rect.left && event.clientX <= iconAreaEnd &&
            event.clientY >= rect.top && event.clientY <= rect.bottom;
    }

    extractPrice(priceElement) {
        const parentItem = priceElement.closest('.nm-easywholesale-item');

        // PRIORITY 1: Check for Massy discounted price INSIDE this variant row
        if (parentItem) {
            const massyDiscount = parentItem.querySelector('.massy-custom-pricing-price-discount');
            if (massyDiscount) {
                const text = massyDiscount.textContent || massyDiscount.innerText;
                const match = text.match(/\$([\d,]+\.?\d*)/);
                if (match) {
                    return parseFloat(match[1].replace(',', ''));
                }
            }
        }

        // PRIORITY 2: Look for Massy discount in MAIN product price area only
        // Use multiple selectors to target the main product section, NOT "You May Also Like" etc.
        const mainPriceSelectors = [
            '.price-wrapper .massy-custom-pricing-price-discount',
            '.product__info-wrapper .massy-custom-pricing-price-discount',
            '.product__info-container .massy-custom-pricing-price-discount',
            '.product-single__meta .massy-custom-pricing-price-discount',
            '[id*="price-template"][id*="main"] .massy-custom-pricing-price-discount',
            '.product-form .massy-custom-pricing-price-discount'
        ];

        for (const selector of mainPriceSelectors) {
            const mainMassyDiscount = document.querySelector(selector);
            if (mainMassyDiscount) {
                const text = mainMassyDiscount.textContent || mainMassyDiscount.innerText;
                const match = text.match(/\$([\d,]+\.?\d*)/);
                if (match) {
                    return parseFloat(match[1].replace(',', ''));
                }
            }
        }

        // PRIORITY 3: Check for data-price attribute (fallback)
        if (parentItem) {
            const priceInput = parentItem.querySelector('[data-price]');
            if (priceInput && priceInput.dataset.price) {
                return parseFloat(priceInput.dataset.price) / 100;
            }
        }

        // PRIORITY 4: Fallback to text extraction from the price element itself
        const text = priceElement.textContent || priceElement.innerText;
        const match = text.match(/\$([\d,]+\.?\d*)/);
        return match ? parseFloat(match[1].replace(',', '')) : 0;
    }

    calculateBreakdown(priceElement) {
        // Get product info from DOM
        const parentItem = priceElement.closest('.nm-easywholesale-item');
        if (!parentItem) {
            return null;
        }

        // Extract SKU and determine region
        const sku = parentItem.dataset.sku || '';
        const region = this.determineRegion(sku, parentItem);
        const productKey = this.getProductKey(sku, parentItem);
        // Look up tax data
        const taxInfo = this.taxData[productKey];
        if (!taxInfo) {
            return null;
        }

        const regionTaxData = taxInfo[region];
        if (!regionTaxData) {
            return null;
        }

        // Get carton size
        const cartonSize = taxInfo.cartonSize || 1;

        // Get live wholesale price from DOM - this is the CARTON price after Massy discount
        // For Nasha 40K: the carton price is now for 4 devices (1 carton)
        const cartonPrice = this.extractPrice(priceElement);
        if (!cartonPrice || cartonPrice <= 0) {
            return null;
        }

        // Taxes are per CARTON
        const cartonFederalTax = regionTaxData.federalTax;
        const cartonProvincialTax = regionTaxData.provincialTax;
        const cartonTotalTax = cartonFederalTax + cartonProvincialTax;

        // Calculate carton device cost: carton price - carton taxes
        const cartonDeviceCost = cartonPrice - cartonTotalTax;

        // Per-unit calculations: divide everything by carton size
        const unitTotal = cartonPrice / cartonSize;
        const unitFederalTax = cartonFederalTax / cartonSize;
        const unitProvincialTax = cartonProvincialTax / cartonSize;
        const unitDeviceCost = cartonDeviceCost / cartonSize;

        return {
            cartonSize: cartonSize,
            region: region,
            // Per unit (1 device)
            unitDeviceCost: unitDeviceCost,
            unitFederalTax: unitFederalTax,
            unitProvincialTax: unitProvincialTax,
            unitTotal: unitTotal,
            // Per carton (e.g., 5 devices)
            cartonDeviceCost: cartonDeviceCost,
            cartonTaxTotal: cartonTotalTax,
            cartonTotal: cartonPrice  // This IS the Massy price
        };
    }

    formatCurrency(amount) {
        return new Intl.NumberFormat('en-CA', {
            style: 'currency',
            currency: 'CAD'
        }).format(amount);
    }

    getProductKey(sku, parentItem = null) {
        // First try to extract from SKU
        if (sku && sku.trim()) {
            if (sku.includes('NASHA40K')) return 'NASHA40K';
            if (sku.includes('NASHA25K')) return 'NASHA25K';
            if (sku.includes('NASHA10K')) return 'NASHA10K';
            if (sku.includes('LIQUID')) return 'NASHA-E-LIQUID';
            if (sku.includes('CodeBlue2mL') || sku.includes('CodeBrown2mL') || sku.includes('CodeCoral2mL') || sku.includes('CodeGreen2mL') || sku.includes('CodeRed2mL')) return 'BOOSTED-BAR-2ML';
            if (sku.includes('BOOSTED6ML')) return 'BOOSTED-ULTRA-POD-1';
            if (sku.includes('ULTRA2POD')) return 'BOOSTED-ULTRA-POD-2';
            if (sku.includes('BOOSTED30K')) return 'BOOSTED30K';
            if (sku.includes('BOOSTSHOT')) return 'BOOSTED-DROP-SHOTS';
            if (sku.includes('7DAY90K') || sku.includes('7D90K') || sku.includes('90K')) return 'BOOSTED-7D-90K';
        }

        // Fallback: Extract from product name/title when SKU is empty
        if (parentItem) {
            const nameElement = parentItem.querySelector('.nm-easywholesale-name');
            const productName = nameElement ? nameElement.textContent.trim().toUpperCase() : '';

            // Also check page title for context
            const pageTitle = document.title.toUpperCase();
            const combinedText = `${productName} ${pageTitle}`;

            if (combinedText.includes('NASHA') && combinedText.includes('40K')) return 'NASHA40K';
            if (combinedText.includes('NASHA') && combinedText.includes('25K')) return 'NASHA25K';
            if (combinedText.includes('NASHA') && combinedText.includes('10K')) return 'NASHA10K';
            if (combinedText.includes('NASHA') && combinedText.includes('LIQUID')) return 'NASHA-E-LIQUID';
            if (combinedText.includes('BOOSTED') && combinedText.includes('ULTRA') && combinedText.includes('6ML')) return 'BOOSTED-ULTRA-POD-1';
            if (combinedText.includes('BOOSTED') && combinedText.includes('ULTRA') && combinedText.includes('II PODS')) return 'BOOSTED-ULTRA-POD-2';
            if (combinedText.includes('BOOSTED') && combinedText.includes('30K')) return 'BOOSTED30K';
            if (combinedText.includes('BOOSTED') && combinedText.includes('BAR') && combinedText.includes('2ML')) return 'BOOSTED-BAR-2ML';
            if (combinedText.includes('BOOST') && combinedText.includes('DROP') && combinedText.includes('SHOTS')) return 'BOOSTED-DROP-SHOTS';
            if (combinedText.includes('7DAY90K') || combinedText.includes('7DAY') && combinedText.includes('90K') || combinedText.includes('7D90K')) return 'BOOSTED-7D-90K';
        }

        return null;
    }

    determineRegion(sku, parentItem) {
        // First priority: Check product title/name for FEDERAL or FED keywords
        const nameElement = parentItem.querySelector('.nm-easywholesale-name');
        if (nameElement) {
            const productName = nameElement.textContent.trim().toUpperCase();
            if (productName.includes('FEDERAL') || productName.includes('[FED]') || productName.includes('FED')) {
                return 'federal';
            }
        }

        // Second priority: Check SKU for federal indicators
        const skuUpper = sku.toUpperCase();
        if (skuUpper.includes('FEDERAL') || skuUpper.includes('[FED]') || skuUpper.includes('FED')) {
            return 'federal';
        }

        // Third priority: Check page URL/title
        if (window.location.pathname.toLowerCase().includes('federal') ||
            document.title.toLowerCase().includes('federal')) {
            return 'federal';
        }

        // Fourth priority: Check for provincial indicators in SKU
        if (skuUpper.includes('[ON]') || skuUpper.includes('[BC]') || skuUpper.includes('[AB]') ||
            skuUpper.includes('[MB]') || skuUpper.includes('[QC]')) {
            return 'provincial';
        }

        // Default to provincial
        return 'provincial';
    }

    showTooltip(event, priceElement) {
        const parentItem = priceElement.closest('.nm-easywholesale-item');
        if (!parentItem) return;

        const sku = parentItem.dataset.sku || '';
        const region = this.determineRegion(sku, parentItem);
        const breakdown = this.calculateBreakdown(priceElement);

        if (!breakdown) {
            return; // No data available for this product
        }

        // Update tier label and accent bar (if customer tier is available from Shopify)
        const tierLabel = this.tooltip.querySelector('#tooltipTierLabel');
        const tooltipContent = this.tooltip.querySelector('.tooltip-content');

        if (tooltipContent) {
            // Remove any existing tier classes from tooltip content
            tooltipContent.classList.remove('gold-tier', 'silver-tier', 'bronze-tier', 'starter-tier');

            if (window.customerTier) {
                // Add tier class to tooltip-content for the accent bar
                tooltipContent.classList.add(window.customerTier + '-tier');

                // Update the inline tier label
                if (tierLabel) {
                    const tierName = window.customerTier.charAt(0).toUpperCase() + window.customerTier.slice(1);
                    tierLabel.textContent = tierName + ' · ';
                    tierLabel.className = 'tier-label ' + window.customerTier;
                }
            } else {
                if (tierLabel) {
                    tierLabel.textContent = '';
                }
            }
        }

        // Update carton quantity badge
        const cartonQtyBadge = this.tooltip.querySelector('.carton-qty-badge');
        if (cartonQtyBadge) {
            cartonQtyBadge.textContent = '×' + breakdown.cartonSize;
        }

        // Update Per Unit section
        const unitDeviceCostEl = this.tooltip.querySelector('.unit-device-cost');
        const unitDeviceOriginalEl = this.tooltip.querySelector('.unit-device-original');
        const unitDeviceDiscountEl = this.tooltip.querySelector('.unit-device-discount');
        const deviceOriginalRow = this.tooltip.querySelector('#deviceOriginalRow');
        const deviceDiscountRow = this.tooltip.querySelector('#deviceDiscountRow');
        const unitFederalTaxEl = this.tooltip.querySelector('.unit-federal-tax');
        const unitProvincialTaxEl = this.tooltip.querySelector('.unit-provincial-tax');
        const unitTotalEl = this.tooltip.querySelector('.unit-total');
        const provincialTaxRow = this.tooltip.querySelector('.provincial-tax-row');

        if (unitDeviceCostEl) unitDeviceCostEl.textContent = this.formatCurrency(breakdown.unitDeviceCost);
        if (unitFederalTaxEl) unitFederalTaxEl.textContent = this.formatCurrency(breakdown.unitFederalTax);
        if (unitTotalEl) unitTotalEl.textContent = this.formatCurrency(breakdown.unitTotal);

        // Show original device price and discount if tier customer with Massy discount
        // Read prices from Massy's DOM elements
        const priceEl = event.target.closest('.nm-easywholesale-price, .nm-easywholesale-item');

        // Try to get prices from DOM elements first (most reliable)
        let originalCartonPrice = null;
        let discountedCartonPrice = null;

        if (priceEl) {
            // Look for Massy's price elements in the DOM
            const originalPriceEl = priceEl.querySelector('.massy-custom-pricing-price-original');
            const discountedPriceEl = priceEl.querySelector('.massy-custom-pricing-price-discount');

            if (originalPriceEl) {
                const match = originalPriceEl.textContent.match(/\$([\d,]+\.?\d*)/);
                if (match) originalCartonPrice = parseFloat(match[1].replace(',', ''));
            }

            if (discountedPriceEl) {
                const match = discountedPriceEl.textContent.match(/\$([\d,]+\.?\d*)/);
                if (match) discountedCartonPrice = parseFloat(match[1].replace(',', ''));
            }

            // Fall back to data attributes if DOM elements not found
            if (!originalCartonPrice && priceEl.dataset?.massyOriginalPrice) {
                originalCartonPrice = parseFloat(priceEl.dataset.massyOriginalPrice);
            }
            if (!discountedCartonPrice && priceEl.dataset?.massyDiscountedPrice) {
                discountedCartonPrice = parseFloat(priceEl.dataset.massyDiscountedPrice);
            }
        }

        // Calculate and display device breakdown if we have both prices and there's a discount
        if (window.customerTier && originalCartonPrice && discountedCartonPrice && originalCartonPrice > discountedCartonPrice) {
            // Calculate original device cost CORRECTLY:
            // Original carton price - total taxes = original device cost (carton)
            // Then divide by carton size for per-unit
            const cartonTaxTotal = breakdown.cartonTaxTotal;  // Already calculated (e.g., $78.40)
            const cartonSize = breakdown.cartonSize;           // e.g., 5

            // Original device cost per unit = (original carton price - taxes) / carton size
            const originalDeviceCost = (originalCartonPrice - cartonTaxTotal) / cartonSize;

            // Current device cost (discounted) is in breakdown
            const discountedDeviceCost = breakdown.unitDeviceCost;

            // Discount amount = original - discounted
            const discountAmount = originalDeviceCost - discountedDeviceCost;

            if (discountAmount > 0.01) {
                if (unitDeviceOriginalEl) unitDeviceOriginalEl.textContent = this.formatCurrency(originalDeviceCost);
                if (unitDeviceDiscountEl) unitDeviceDiscountEl.textContent = '-' + this.formatCurrency(discountAmount);
                if (deviceOriginalRow) deviceOriginalRow.style.display = 'flex';
                if (deviceDiscountRow) deviceDiscountRow.style.display = 'flex';
            } else {
                if (deviceOriginalRow) deviceOriginalRow.style.display = 'none';
                if (deviceDiscountRow) deviceDiscountRow.style.display = 'none';
            }
        } else {
            // Hide original/discount rows for non-tier customers or no discount
            if (deviceOriginalRow) deviceOriginalRow.style.display = 'none';
            if (deviceDiscountRow) deviceDiscountRow.style.display = 'none';
        }

        // Show/hide provincial tax based on region
        if (region === 'federal') {
            if (provincialTaxRow) provincialTaxRow.style.display = 'none';
        } else {
            if (provincialTaxRow) provincialTaxRow.style.display = 'flex';
            if (unitProvincialTaxEl) unitProvincialTaxEl.textContent = this.formatCurrency(breakdown.unitProvincialTax);
        }

        // Update Per Carton section
        const cartonDeviceCostEl = this.tooltip.querySelector('.carton-device-cost');
        const cartonTaxEl = this.tooltip.querySelector('.carton-tax');
        const cartonTotalEl = this.tooltip.querySelector('.carton-total');

        if (cartonDeviceCostEl) cartonDeviceCostEl.textContent = this.formatCurrency(breakdown.cartonDeviceCost);
        if (cartonTaxEl) cartonTaxEl.textContent = this.formatCurrency(breakdown.cartonTaxTotal);
        if (cartonTotalEl) cartonTotalEl.textContent = this.formatCurrency(breakdown.cartonTotal);

        // Update Savings section (reuse the prices we already fetched above)
        const savingsSection = this.tooltip.querySelector('#tooltipSavingsSection');
        const savingsOriginal = this.tooltip.querySelector('.savings-original');
        const savingsDiscounted = this.tooltip.querySelector('.savings-discounted');
        const savingsAmount = this.tooltip.querySelector('.savings-amount');

        if (savingsSection && window.customerTier && originalCartonPrice && discountedCartonPrice && originalCartonPrice > discountedCartonPrice) {
            const savings = originalCartonPrice - discountedCartonPrice;

            if (savingsOriginal) savingsOriginal.textContent = this.formatCurrency(originalCartonPrice);
            if (savingsDiscounted) savingsDiscounted.textContent = this.formatCurrency(discountedCartonPrice);
            if (savingsAmount) savingsAmount.textContent = '-' + this.formatCurrency(savings);

            savingsSection.style.display = 'block';
        } else if (savingsSection) {
            savingsSection.style.display = 'none';
        }

        // Position and show tooltip
        this.positionTooltip(event);
        this.tooltip.style.display = 'block';
    }

    hideTooltip() {
        if (this.tooltip) {
            this.tooltip.style.display = 'none';
        }
    }

    positionTooltip(event) {
        const tooltipRect = this.tooltip.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;

        // Position right beside the mouse cursor
        let left = event.clientX + 15;
        let top = event.clientY - (tooltipRect.height / 2);

        // Adjust if tooltip goes off right edge
        if (left + tooltipRect.width > viewportWidth) {
            left = event.clientX - tooltipRect.width - 15;
        }

        // Adjust if tooltip goes off top edge
        if (top < scrollY) {
            top = event.clientY + 15;
        }

        // Adjust if tooltip goes off bottom edge
        if (top + tooltipRect.height > scrollY + viewportHeight) {
            top = event.clientY - tooltipRect.height - 15;
        }

        // Ensure we don't go off left edge
        if (left < scrollX) {
            left = scrollX + 10;
        }

        this.tooltip.style.left = `${left}px`;
        this.tooltip.style.top = `${top}px`;
        this.tooltip.style.position = 'fixed';
    }

    observeDOM() {
        // Watch for changes in the DOM to handle dynamically loaded wholesale content
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    // Check if wholesale elements were added
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1 &&
                            (node.classList && (node.classList.contains('nm-easywholesale-container') ||
                                node.classList.contains('nm-wholesale-table-container')) ||
                                node.querySelector && (node.querySelector('.nm-easywholesale-price') ||
                                    node.querySelector('.nm-wholesale-header-price')))) {
                            // New wholesale content loaded, events already attached via delegation
                        }
                    });
                }
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

// Initialize when DOM is ready or immediately if already loaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new WholesalePriceTooltip();
    });
} else {
    new WholesalePriceTooltip();
}
